import { Box, Button, Card, CardContent, Divider, IconButton, Popover, Tooltip } from "@mui/material";
import Edit from "@mui/icons-material/Edit";
import {
  displayCaseOutcomeStatus,
  displayDate,
  displayDateAndTimeCompact,
  displayDateString,
  formatRelativeTime,
  INTERNAL_USER_NAME,
  isNotUndefined,
  PropertyCell as PropertyCellType,
  PropertyDisplayGroup,
} from "Backend";
import { Case, CaseInfo, CaseReviewView, CustomCaseProperty, NamedUser } from "generated/graphql";
import { Dispatch, SetStateAction, useMemo, useState } from "react";
import { useAppSelector } from "redux/hooks";
import { selectCaseCalcs, selectCaseInsightsPending, selectCaseReviews } from "redux/slices/case/";
import { P } from "theme/typography";
import { TextTab } from "components/TextTab";
import { selectCaseInsightsEnabled } from "redux/slices/clientLens";
import theme, { EllipsisSX } from "theme";
import { useRunAutoScripts } from "helpers/hooks/useRunAutoScripts";
import { ArrowDropDown, ArrowRight, AutoAwesome, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import { PCopy } from "components/PCopy";
import LoadingPattern from "components/LoadingPattern";
import { usePopover } from "helpers/hooks/usePopover";
import {
  Timeline,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineItem,
  TimelineOppositeContent,
  TimelineSeparator,
  timelineOppositeContentClasses,
} from "@mui/lab";
import { parseISO } from "date-fns";
import { FullName } from "components/FullName";
import { CASE_DISPLAY_GROUP_DEFAULT_NAME } from "routes/lens-editor/custom-properties/case-display-groups.loader";
import DescriptionTooltip from "components/FormField/DescriptionTooltip";
import { stripNull } from "helpers/coll";
import {
  CaseDisplayGroupWithPropertyValues,
  useCaseInfoDisplayGroups,
  UseCaseInfoDisplayGroups,
} from "routes/lenses/lens/case/useCaseInfoDisplayGroups";
import CaseOutcomeStatus from "routes/lenses/lens/case/CaseOutcomeStatus";
import { CaseInsightsRefreshTooltip } from "routes/lenses/lens/case/CaseInsightsRefreshTooltip";
import CaseSummary from "routes/lenses/lens/case/Summary";
import EditCaseInfoDialog from "routes/lenses/lens/case/EditCaseInfoDialog";
import { HeaderCard, HeaderCardTitle } from "components/HeaderCard";

type TextTab = "CASE_INFO" | "SUMMARY";

function CaseReviews({ reviews }: { reviews: CaseReviewView[] }) {
  const latestReviewer = reviews[0];
  const popover = usePopover();

  return (
    <Box sx={{ display: "flex" }}>
      <Box sx={{ display: "flex", alignItems: "center" }}>
        {latestReviewer && (
          <P variant="xs" sx={{ color: theme.palette.grey[200] }}>
            <FullName userId={latestReviewer.userId} defaultName={INTERNAL_USER_NAME} />:{" "}
            {formatRelativeTime(latestReviewer.timestamp)}
          </P>
        )}
        {reviews.length > 1 && (
          <>
            <Tooltip title="See all reviews">
              <IconButton onClick={(evt) => popover.onPopoverOpen(evt.currentTarget)} size="small">
                {popover.isOpen ? <ArrowDropDown /> : <ArrowRight />}
              </IconButton>
            </Tooltip>
            <Popover
              open={popover.isOpen}
              onClose={popover.onPopoverClose}
              anchorEl={popover.anchorEl}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "left",
              }}>
              <Card>
                <CardContent
                  sx={{ display: "flex", flexDirection: "column", gap: 1, pb: "16px!important", minWidth: 400 }}>
                  <P variant="baseSemiBold">All Reviews</P>
                  <Timeline
                    sx={{
                      [`& .${timelineOppositeContentClasses.root}`]: {
                        paddingTop: "10px",
                      },
                      "& .MuiTimelineItem-root": {
                        minHeight: 50,
                      },
                      "& .MuiTimelineContent-root": {
                        paddingTop: "10px",
                      },
                      p: 0,
                    }}>
                    {reviews.map((review, index) => (
                      <TimelineItem key={index}>
                        <TimelineOppositeContent title={displayDateAndTimeCompact(review.timestamp)}>
                          {displayDate(parseISO(review.timestamp))}
                        </TimelineOppositeContent>
                        <TimelineSeparator>
                          <TimelineDot />
                          {index !== reviews.length - 1 && <TimelineConnector />}
                        </TimelineSeparator>
                        <TimelineContent>
                          <FullName userId={review.userId} defaultName={INTERNAL_USER_NAME} />
                        </TimelineContent>
                      </TimelineItem>
                    ))}
                  </Timeline>
                </CardContent>
              </Card>
            </Popover>
          </>
        )}
      </Box>
    </Box>
  );
}

function PropertyCell({ cell }: { cell: PropertyCellType }) {
  return !cell.hidden && (cell.textValue || cell.notCopyable) ? (
    <Box
      key={cell.prop}
      sx={{ p: 1, display: "flex", flexDirection: "column", justifyContent: "center", maxHeight: "100px" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1,
        }}>
        <P variant="sm" title={cell.prop} sx={{ fontWeight: 500, ...EllipsisSX }}>
          {cell.prop}
        </P>
        <DescriptionTooltip description={cell.description} />
      </Box>

      {cell.notCopyable ? (
        <P variant="sm" sx={{ fontWeight: 500, color: theme.palette.grey[200], ...EllipsisSX }}>
          {cell.value || "\u200b"}
        </P>
      ) : (
        <PCopy
          text={cell.textValue || ""}
          pProps={{
            variant: "sm",
          }}
          sx={{ fontWeight: 500, color: theme.palette.grey[200], ...EllipsisSX }}>
          {cell.value || "\u200b"}
        </PCopy>
      )}
    </Box>
  ) : (
    <></>
  );
}

function toPropertyDisplayGroup(group: CaseDisplayGroupWithPropertyValues): PropertyDisplayGroup {
  return {
    name: group.group.name,
    description: group.group.description || undefined,
    cells: group.propertyValues.map((pv) => ({
      prop: (pv.caseProperty?.displayName || pv.caseCalc?.displayName) ?? "",
      description: stripNull(pv.caseProperty?.description || pv.caseCalc?.description),
      value: pv.value,
      textValue: pv.value,
      hidden: pv.caseProperty?.isHidden,
    })),
    canEdit: !!group.propertyValues.find((pv) => pv.caseProperty !== undefined),
  };
}

function CaseInfoMatrix({
  mycase,
  setEditMode,
  setGroupToEdit,
  caseDisplayGroupsWithPropertyValues,
}: {
  mycase: Case;
  setEditMode?: Dispatch<SetStateAction<boolean>>;
  setGroupToEdit?: (group: PropertyDisplayGroup | undefined) => void;
  caseDisplayGroupsWithPropertyValues: UseCaseInfoDisplayGroups;
}) {
  const caseInfo = mycase.info;
  const caseInsightsPending = useAppSelector(selectCaseInsightsPending);
  const reviews = useAppSelector(selectCaseReviews);
  const caseInsightsEnabled = useAppSelector(selectCaseInsightsEnabled);
  const runAutoScripts = useRunAutoScripts(mycase.id);
  const name = `${caseInfo.claimantGivenName} ${caseInfo.claimantSurname}`;
  const dateCellTitle = caseInfo.dateOfDeath ? "DOB - DOD" : "Date of Birth";
  const date = `${displayDateString(mycase.info?.dateOfBirth)}${
    mycase.info?.dateOfDeath ? " - " + displayDateString(mycase.info?.dateOfDeath) : ""
  }`;
  const propertyCells: PropertyCellType[] = [{ prop: "Name", value: name, textValue: name }];
  const [showEmptyGroups, setShowEmptyGroups] = useState(false);

  if (mycase.info?.stateOfResidence) {
    propertyCells.push({
      prop: "State",
      value: mycase.info?.stateOfResidence,
      textValue: mycase.info?.stateOfResidence,
    });
  }
  if (mycase.info?.coCounsel) {
    propertyCells.push({ prop: "Co-Counsel", value: mycase.info?.coCounsel, textValue: mycase.info?.coCounsel });
  }
  if (date) {
    propertyCells.push({
      prop: dateCellTitle,
      value: date,
      textValue: date,
    });
  }
  if (mycase.outcomeStatus) {
    propertyCells.push({
      prop: "Outcome",
      value: <CaseOutcomeStatus status={mycase.outcomeStatus} onlyStatus={true} />,
      textValue: displayCaseOutcomeStatus(mycase.outcomeStatus),
    });
  }
  if (reviews.length > 0) {
    propertyCells.push({
      prop: "Latest Reviewer",
      value: <CaseReviews reviews={reviews} />,
      notCopyable: true,
    });
  }
  propertyCells.push(
    ...(caseDisplayGroupsWithPropertyValues.defaultGroup?.propertyValues.map((cpv) => ({
      prop: (cpv.caseProperty?.displayName || cpv.caseCalc?.displayName) ?? "",
      value: cpv.value,
      textValue: cpv.value,
      hidden: cpv.caseProperty?.isHidden,
    })) ?? [])
  );

  const visibleGroups: PropertyDisplayGroup[] =
    caseDisplayGroupsWithPropertyValues.visibleGroups.map(toPropertyDisplayGroup);
  const emptyGroups: PropertyDisplayGroup[] =
    caseDisplayGroupsWithPropertyValues.emptyGroups.map(toPropertyDisplayGroup);

  return (
    <div className="max-h-[304px] overflow-y-auto max-w-[826px]">
      <Box sx={{ display: "grid", gridTemplateColumns: "1fr 50px" }}>
        <Box
          sx={{
            display: "grid",
            overflowY: "auto",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, max-content)",
          }}>
          {propertyCells.map((cell) => (
            <PropertyCell key={cell.prop} cell={cell} />
          ))}
        </Box>
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", pr: 0.5, pt: 0.5 }}>
          {setEditMode && setGroupToEdit && (
            <>
              <IconButton
                data-pendo="edit-case-info"
                onClick={() => {
                  setGroupToEdit(undefined);
                  setEditMode(true);
                }}
                size="small">
                <Edit />
              </IconButton>
              {caseInsightsEnabled && (
                <CaseInsightsRefreshTooltip>
                  <Box
                    sx={{
                      width: "50px",
                      height: "50px",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                    }}>
                    <IconButton onClick={runAutoScripts} size="large" disabled={caseInsightsPending}>
                      {caseInsightsPending ? <LoadingPattern size={20} /> : <AutoAwesome />}
                    </IconButton>
                  </Box>
                </CaseInsightsRefreshTooltip>
              )}
            </>
          )}
        </Box>
      </Box>
      {visibleGroups.map((group) => (
        <Box
          key={group.name}
          sx={{
            display: "grid",
            overflowY: "auto",
            gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
            gridTemplateRows: "minmax(0, max-content)",
            my: 1,
          }}>
          <Box
            sx={{
              gridColumn: "1 / span 3",
              pl: 1,
              borderBottom: `1px solid ${theme.palette.grey[100]}`,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "flex-end",
            }}>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 1,
              }}>
              <P variant="lgBold">{group.name}</P>
              <DescriptionTooltip description={group.description} />
            </Box>

            {group.canEdit && setEditMode && setGroupToEdit && (
              <IconButton
                data-pendo="case-info-edit-group"
                sx={{ mr: 1.5 }}
                onClick={() => {
                  setGroupToEdit(group);
                  setEditMode(true);
                }}
                size="small">
                <Edit />
              </IconButton>
            )}
          </Box>
          {group.cells.map((cell) => (
            <PropertyCell key={cell.prop} cell={cell} />
          ))}
        </Box>
      ))}
      {emptyGroups.length > 0 && (
        <Button
          onClick={() => setShowEmptyGroups((prev) => !prev)}
          size="small"
          color="default"
          variant="text"
          data-pendo="case-info-show-hide-groups"
          endIcon={showEmptyGroups ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
          sx={{ textTransform: "capitalize!important", ml: "6px" }}>
          {showEmptyGroups ? "hide" : "show"} empty groups
        </Button>
      )}
      {showEmptyGroups ? (
        <Box sx={{ pl: 2 }}>
          {emptyGroups.map((group) => (
            <Box
              key={group.name}
              sx={{
                display: "grid",
                overflowY: "auto",
                gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                gridTemplateRows: "minmax(0, max-content)",
                my: 1,
              }}>
              <Box
                sx={{
                  gridColumn: "1 / span 3",
                  pl: 1,
                  borderBottom: `1px solid ${theme.palette.grey[100]}`,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "flex-end",
                }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}>
                  <P variant="lgBold">{group.name}</P>
                  <DescriptionTooltip description={group.description} />
                </Box>
                {setEditMode && setGroupToEdit && (
                  <IconButton
                    data-pendo="case-info-edit-empty-group"
                    sx={{ mr: 1.5 }}
                    onClick={() => {
                      setGroupToEdit(group);
                      setEditMode(true);
                    }}
                    size="small">
                    <Edit />
                  </IconButton>
                )}
              </Box>
              {group.cells.map((cell) => (
                <PropertyCell key={cell.prop} cell={cell} />
              ))}
            </Box>
          ))}
        </Box>
      ) : (
        <></>
      )}
    </div>
  );
}

function CaseInfoCard(props: {
  mycase: Case;
  assignees: NamedUser[];
  activeTab: TextTab;
  setEditMode: Dispatch<SetStateAction<boolean>>;
  editMode: boolean;
  edited?: (info: CaseInfo, userAssignee?: NamedUser) => void;
  groupToEdit: PropertyDisplayGroup | undefined;
  setGroupToEdit: (group: PropertyDisplayGroup | undefined) => void;
}) {
  const { mycase, assignees, activeTab, editMode, setEditMode, edited, groupToEdit, setGroupToEdit } = props;
  const calcs = useAppSelector(selectCaseCalcs);
  const caseInfoDisplayGroups = useCaseInfoDisplayGroups(mycase, calcs);
  const editableProperties: CustomCaseProperty[] = useMemo(() => {
    return (
      caseInfoDisplayGroups.allGroups.find(
        (g) => g.group.name === (groupToEdit?.name || CASE_DISPLAY_GROUP_DEFAULT_NAME)
      ) || { propertyValues: [] }
    )?.propertyValues
      .filter((pv) => !pv.caseProperty?.isHidden)
      .map((pv) => pv.caseProperty)
      .filter(isNotUndefined);
  }, [caseInfoDisplayGroups, groupToEdit]);

  return (
    <>
      {activeTab === "CASE_INFO" && (
        <CaseInfoMatrix
          caseDisplayGroupsWithPropertyValues={caseInfoDisplayGroups}
          mycase={mycase}
          setEditMode={setEditMode}
          setGroupToEdit={setGroupToEdit}
        />
      )}
      {editMode && activeTab === "CASE_INFO" && (
        <EditCaseInfoDialog
          canEditCaseInfo={groupToEdit === undefined}
          open={editMode}
          close={() => setEditMode(false)}
          caseProperties={editableProperties || []}
          assignees={assignees}
          mycase={mycase}
          edited={edited}
          group={groupToEdit}
        />
      )}
      {activeTab === "SUMMARY" && <CaseSummary mycase={mycase} />}
    </>
  );
}

export default function SBRCaseInfoBox({
  ccase,
  assignees,
  initialEditMode,
  edited,
}: {
  ccase: Case;
  assignees: NamedUser[];
  initialEditMode?: boolean;
  edited?: (info: CaseInfo, userAssignee?: NamedUser) => void;
}) {
  const [editMode, setEditMode] = useState<boolean>(initialEditMode || false);
  const [groupToEdit, setGroupToEdit] = useState<PropertyDisplayGroup | undefined>(undefined);
  const [activeTab, setActiveTab] = useState<TextTab>("CASE_INFO");

  return (
    <HeaderCard
      title={
        <HeaderCardTitle className="flex">
          <TextTab
            variant="subtitle1"
            active={activeTab === "CASE_INFO"}
            click={() => {
              setEditMode(false);
              setActiveTab("CASE_INFO");
            }}>
            Case Info
          </TextTab>
          <Divider sx={{ mx: 1, height: 20 }} orientation="vertical" />
          <TextTab
            variant="subtitle1"
            active={activeTab === "SUMMARY"}
            click={() => {
              setEditMode(false);
              setActiveTab("SUMMARY");
            }}>
            Summary
          </TextTab>
        </HeaderCardTitle>
      }>
      <CaseInfoCard
        groupToEdit={groupToEdit}
        setGroupToEdit={setGroupToEdit}
        editMode={editMode}
        edited={edited}
        setEditMode={setEditMode}
        activeTab={activeTab}
        mycase={ccase}
        assignees={assignees}
      />
    </HeaderCard>
  );
}
